import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
	Take,
	Reporting,
	NewTakePayload,
	CompleteTakePayload,
	CognitoUser,
	Client,
} from '@mira-avant/mtlibs';
import { AuthService } from './auth.service';
import { ClientService } from './client.service';
import { ApiService } from './api.service';

export interface TakeResponse {
	take: Take;
	reporting: Reporting;
}

@Injectable({
	providedIn: 'root',
})
export class TakeService {
	private readonly BASE_PATH = 'takes';

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(
		private api: ApiService,
		private authService: AuthService,
		private clientService: ClientService
	) {}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async createTake$(
		takeData: Omit<NewTakePayload, 'student_cognito_uuid' | 'client_uuid'>
	): Promise<TakeResponse> {
		return this.withLoading(async () => {
			const user: CognitoUser | null = await firstValueFrom(this.authService.user$);

			if (!user) {
				throw new Error('No user found');
			}

			const client: Client = await firstValueFrom(this.clientService.selectedClient$);

			const result = await firstValueFrom(
				this.api.post<TakeResponse>(`${this.BASE_PATH}/new-take`, {
					...takeData,
					student_cognito_uuid: user.uuid,
					client_uuid: client.uuid,
				})
			);

			return result;
		});
	}

	public async completeTake$(
		completeData: Omit<CompleteTakePayload, 'student_cognito_uuid' | 'client_uuid'>
	): Promise<TakeResponse> {

		return this.withLoading(async () => {
			const user: CognitoUser | null = await firstValueFrom(this.authService.user$);

			if (!user) {
				throw new Error('No user found');
			}

			const client: Client = await firstValueFrom(this.clientService.selectedClient$);

			const result = await firstValueFrom(
				this.api.post<TakeResponse>(`${this.BASE_PATH}/complete-take`, {
					...completeData,
					student_cognito_uuid: user.uuid,
					client_uuid: client.uuid,
				})
			);

			return result;
		});
	}
}
